import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Send, ArrowRightCircle } from "lucide-react";
import { api } from "@/lib/api";
import { DeleteDocButton } from "@/components/procurement/DeleteDocButton";
import { ApprovalReviewButton } from "@/components/procurement/ApprovalReview";
import { ItemPicker } from "@/components/procurement/ItemPicker";
import { ProjectFilter, useProjectFilter } from "@/components/procurement/ProjectFilter";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

const PROCUREMENT_WRITE_ROLES = ["ORG_ADMIN", "PROCUREMENT"];
const EMPTY_ITEM = { description: "", quantity: "", estimated_rate: "" };

export default function PurchaseRequisitions() {
  const pf = useProjectFilter();
  const { user } = useAuth();
  const canWrite = PROCUREMENT_WRITE_ROLES.includes(user?.role);
  const { companies } = useCompany() || {};
  const [requisitions, setRequisitions] = useState([]);
  const [indents, setIndents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, indentRes] = await Promise.all([
        api.get("/procurement/requisitions"), api.get("/procurement/indents").catch(() => ({ data: [] })),
      ]);
      setRequisitions(data);
      setIndents(indentRes.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const companyName = (id) => companies?.find((c) => c.id === id)?.name || "—";

  const submit = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/procurement/requisitions/${r.id}/submit`);
      toast.success("Requisition submitted");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const convert = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/procurement/requisitions/${r.id}/convert-to-rfq`, { rfq_date: new Date().toISOString().slice(0, 10) });
      toast.success("Converted to RFQ");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="purchase-requisitions-page">
      <PageHeader title="Purchase Requisitions" description="Formal, reviewed requisitions — converted from an approved indent, or created directly." action={<div className="flex flex-wrap items-center justify-end gap-2"><ProjectFilter filter={pf} />{canWrite && <CreateRequisitionDialog pfProjects={pf.projects} companies={companies} onCreated={load} />}</div>} />
      {!loading && requisitions.length === 0 ? (
        <EmptyState title="No purchase requisitions yet" testId="requisitions-empty-state" />
      ) : (
        <div className="space-y-3">
          {pf.apply(requisitions, (r) => r.project_id).map((r) => (
            <div key={r.id} className="rounded-xl border border-border bg-card p-4" data-testid={`requisition-card-${r.number}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-mono text-xs text-muted-foreground">{r.number}</p>
                  <p className="text-sm text-foreground">{companyName(r.company_id)} · {r.requisition_date}</p>
                  {r.indent_id && (
                    <p className="mt-0.5 text-[11px] text-sky-600 dark:text-sky-400">From indent {indents.find((i) => i.id === r.indent_id)?.number || "—"}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill value={r.status} />
                  <ApprovalReviewButton docType="PR" docId={r.id} onChanged={load} />
                      {canWrite && <DeleteDocButton path={`/procurement/requisitions/${r.id}`} label="Requisition" number={r.number} status={r.status} onDeleted={load} />}
                  {canWrite && r.status === "DRAFT" && (
                    <Button size="sm" variant="outline" disabled={busyId === r.id} onClick={() => submit(r)} data-testid={`submit-requisition-button-${r.number}`}>
                      <Send className="h-3.5 w-3.5" /> Submit
                    </Button>
                  )}
                  {canWrite && r.status === "APPROVED" && (
                    <Button size="sm" variant="outline" disabled={busyId === r.id} onClick={() => convert(r)} data-testid={`convert-requisition-button-${r.number}`}>
                      <ArrowRightCircle className="h-3.5 w-3.5" /> Convert to RFQ
                    </Button>
                  )}
                </div>
              </div>
              <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                {r.items.map((item) => <li key={item.id}>{item.description} — {item.quantity} @ {item.estimated_rate || "—"}</li>)}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateRequisitionDialog({ companies, pfProjects, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", requisition_date: "", remarks: "" });
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const updateItem = (idx, field, value) => setItems(items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  // Picking a catalogue item fills description, unit and price (price can still be changed).
  const pickItem = (idx, item) => setItems(items.map((it, i) => (i === idx ? {
    ...it, item_id: item.id, description: item.name, uom_id: item.uom_id || it.uom_id || null, estimated_rate: Number(item.standard_rate) > 0 ? String(item.standard_rate) : it.estimated_rate,
  } : it)));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/procurement/requisitions", { ...form, project_id: form.project_id || null, items: items.map((it) => ({ ...it, estimated_rate: it.estimated_rate || null })) });
      toast.success("Purchase requisition created");
      setForm({ company_id: "", requisition_date: "", remarks: "" });
      setItems([{ ...EMPTY_ITEM }]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-requisition-button"><Plus className="h-4 w-4" /> New Requisition</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Purchase Requisition</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-requisition-form">
          <div>
            <Label>Project</Label>
            <Select value={form.project_id || ""} onValueChange={(v) => setForm({ ...form, project_id: v })}>
              <SelectTrigger className="mt-1.5" data-testid="requisitions-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>{(pfProjects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Company</Label>
              <Select required value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="requisition-company-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input required type="date" data-testid="requisition-date-input" value={form.requisition_date} onChange={set("requisition_date")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Items</Label>
            <div className="mt-1.5 space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`requisition-item-row-${idx}`}>
                  <ItemPicker required value={it.description} onChange={(v) => updateItem(idx, "description", v)} onPick={(item) => pickItem(idx, item)} data-testid={`requisition-item-desc-input-${idx}`} />
                  <Input required type="number" step="0.001" placeholder="Qty" className="w-24" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} data-testid={`requisition-item-qty-input-${idx}`} />
                  {items.length > 1 && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => setItems(items.filter((_, i) => i !== idx))} data-testid={`remove-requisition-item-button-${idx}`}>
                      <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setItems([...items, { ...EMPTY_ITEM }])} data-testid="add-requisition-item-button">
              <Plus className="h-3.5 w-3.5" /> Add item
            </Button>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-requisition-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
